import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Typography, InputAdornment,
  Pagination, Skeleton, Alert, Tooltip, IconButton, useMediaQuery,
  Select, MenuItem, FormControl, InputLabel, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Stack, Slide, Paper as MuiPaper,
  ListItemText, Divider, CircularProgress, ListSubheader,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search, Warning, Star, ArrowUpward, ArrowDownward, Download,
  Assignment, FilterList, ViewList, ViewModule, Park,
  ChevronRight, CheckBox as CheckBoxIcon, Delete,
  Schedule, CheckCircle, RemoveCircleOutline, SearchOff, Add,
  ContentCut, FolderOpen, Edit, Close, EventBusy,
} from '@mui/icons-material';
import api from '../services/api';
import { treesAPI, measuresAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';
import { MEASURE_TYPE_OPTIONS, MEASURE_TYPE_GROUPS, VERKEHRSSICHERHEIT_OPTIONS, getVerkehrssicherheitColor } from '../data/treeFieldOptions';

// Zustand color mapping
function getZustandColor(zustand: string | null): string {
  if (!zustand) return '#78909c';
  const z = zustand.toLowerCase();
  if (z.includes('abgestorben')) return '#424242';
  if (z.includes('stark') && z.includes('geschä')) return '#c62828';
  if (z.includes('stärker') || z.includes('staerker')) return '#e65100';
  if (z.includes('leicht') && z.includes('geschä')) return '#f9a825';
  if (z.includes('gesund')) return '#2e7d32';
  return '#78909c';
}

// Mobile Tree Card
function TreeCard({ tree, onClick }: { tree: any; onClick: () => void }) {
  const hatSchaden = tree.offene_schaeden > 0 || tree.scha_o_1;
  return (
    <Box onClick={onClick} sx={{
      p: 1.5, borderRadius: 2, bgcolor: 'white', cursor: 'pointer',
      border: '1px solid', borderColor: hatSchaden ? '#ffcc80' : '#e8e8e8',
      borderLeft: '4px solid', borderLeftColor: getZustandColor(tree.zustand),
      display: 'flex', alignItems: 'center', gap: 1.5,
      opacity: tree.gefaellt ? 0.55 : 1,
      '&:active': { bgcolor: '#f5f5f5' },
    }}>
      {/* Tree icon */}
      <Box sx={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        bgcolor: getZustandColor(tree.zustand) + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Park sx={{ color: getZustandColor(tree.zustand), fontSize: 22 }} />
      </Box>

      {/* Main info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.88rem' }}>
            {tree.baumnummer}
          </Typography>
          {tree.gefaellt && <Chip label="Gefällt" size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
          {tree.is_baumgruppe && <Chip label={`Gruppe${tree.gruppe_anzahl ? ` · ${tree.gruppe_anzahl}` : ''}`} size="small" color="primary" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />}
          {tree.verkehrssicherheit && <Chip label={tree.verkehrssicherheit} size="small" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, color: getVerkehrssicherheitColor(tree.verkehrssicherheit).color, bgcolor: getVerkehrssicherheitColor(tree.verkehrssicherheit).bg }} />}
        </Box>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.78rem' }}>
          {tree.is_baumgruppe ? 'Baumgruppe' : (tree.baumart || 'Unbekannt')}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.72rem' }}>
          {[tree.strasse, tree.ort].filter(Boolean).join(', ') || '–'}
        </Typography>
      </Box>

      {/* Status icons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.3 }}>
          {hatSchaden && (
            <Chip icon={<Warning sx={{ fontSize: '14px !important' }} />}
              label={tree.offene_schaeden || '!'} size="small"
              sx={{ height: 22, fontSize: '0.68rem', bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700, '& .MuiChip-icon': { color: '#e65100' } }} />
          )}
          {!hatSchaden && tree.bestaetigte_schaeden > 0 && (
            <Chip icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
              label={`${tree.bestaetigte_schaeden} geprüft`} size="small"
              sx={{ height: 22, fontSize: '0.68rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, '& .MuiChip-icon': { color: '#2e7d32' } }} />
          )}
          {tree.offene_massnahmen > 0 && (
            <Chip icon={<Star sx={{ fontSize: '14px !important' }} />}
              label={tree.offene_massnahmen} size="small"
              sx={{ height: 22, fontSize: '0.68rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, '& .MuiChip-icon': { color: '#1565c0' } }} />
          )}
          {tree.kontrollen_count > 0 && (
            <Chip icon={<Assignment sx={{ fontSize: '14px !important' }} />}
              label={tree.kontrollen_count} size="small"
              sx={{ height: 22, fontSize: '0.68rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, '& .MuiChip-icon': { color: '#2e7d32' } }} />
          )}
        </Box>
        {tree.letzte_kontrolle && (
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            Kontrolle: {new Date(tree.letzte_kontrolle).toLocaleDateString('de-DE')}
          </Typography>
        )}
      </Box>

      <ChevronRight sx={{ color: '#ccc', fontSize: 20, flexShrink: 0 }} />
    </Box>
  );
}

export default function Trees() {
  const navigate = useNavigate();
  const { canEdit, isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [trees, setTrees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });

  // Filter-State mit sessionStorage-Persistierung – Filter überleben Navigation in dieser Browser-Session
  const FILTER_KEY = 'baumwaechter.trees.filters';
  const loadSavedFilters = () => {
    try {
      const raw = sessionStorage.getItem(FILTER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const saved = loadSavedFilters() || {};

  const [search, setSearch] = useState<string>(saved.search || '');
  // v1.3.0: Status-basierte Filter (single-select pro Gruppe)
  const [massnahmenStatus, setMassnahmenStatus] = useState<'all' | 'open' | 'done' | 'none'>(saved.massnahmenStatus || 'all');
  const [schaedenStatus, setSchaedenStatus] = useState<'all' | 'open' | 'behoben' | 'none'>(saved.schaedenStatus || 'all');
  const [kontrollenStatus, setKontrollenStatus] = useState<'all' | 'aktuell' | 'soon' | 'overdue' | 'never' | 'vorhanden' | 'keine'>(saved.kontrollenStatus || 'all');
  const [filterGefaellt, setFilterGefaellt] = useState<boolean>(saved.filterGefaellt || false);
  const [filterZustand, setFilterZustand] = useState<string>(saved.filterZustand || '');
  const [filterOrt, setFilterOrt] = useState<string>(saved.filterOrt || '');
  const [filterMassnahmeArt, setFilterMassnahmeArt] = useState<string>(saved.filterMassnahmeArt || ''); // IMP-012: Filter nach Maßnahmen-Art
  const [filterVerkehrssicherheit, setFilterVerkehrssicherheit] = useState<string>(saved.filterVerkehrssicherheit || ''); // IMP-013
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(isMobile ? 'cards' : 'table');
  const [sortBy, setSortBy] = useState<string>(saved.sortBy || 'updated_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(saved.sortOrder || 'DESC');

  // Filter bei Änderung in sessionStorage cachen
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_KEY, JSON.stringify({
        search, massnahmenStatus, schaedenStatus, kontrollenStatus,
        filterGefaellt, filterZustand, filterOrt, filterMassnahmeArt, filterVerkehrssicherheit, sortBy, sortOrder,
      }));
    } catch {/* still */}
  }, [search, massnahmenStatus, schaedenStatus, kontrollenStatus, filterGefaellt, filterZustand, filterOrt, filterMassnahmeArt, filterVerkehrssicherheit, sortBy, sortOrder]);

  const [filterOptions, setFilterOptions] = useState<any>({ orte: [], zustaende: [] });

  // Bulk actions
  const [selected, setSelected] = useState<Map<string, any>>(new Map());
  const [bulkDialog, setBulkDialog] = useState<'measure' | 'delete' | 'status' | 'lv' | 'fell' | null>(null);
  const [bulkMeasure, setBulkMeasure] = useState({ measure_type: '', description: '', planned_date: '', assigned_to: '' });
  const [selectableUsers, setSelectableUsers] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    api.get('/users/selectable').then(r => setSelectableUsers(r.data)).catch(() => setSelectableUsers([]));
  }, []);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLvId, setBulkLvId] = useState('');
  const [availableLVs, setAvailableLVs] = useState<any[]>([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // LV-Liste für Bulk-Aktion laden (lazy)
  const loadLVs = async () => {
    if (availableLVs.length > 0) return;
    try { const res = await api.get('/lv'); setAvailableLVs(res.data); }
    catch {/* still */}
  };

  const toggleSelect = (tree: any) => {
    setSelected(prev => { const next = new Map(prev); if (next.has(tree.id)) next.delete(tree.id); else next.set(tree.id, tree); return next; });
  };
  // "Alle auswählen" bezieht sich auf die aktuelle Seite, akkumuliert aber über
  // Seiten hinweg (die Auswahl bleibt beim Blättern erhalten).
  const currentPageAllSelected = trees.length > 0 && trees.every(t => selected.has(t.id));
  const currentPageSomeSelected = trees.some(t => selected.has(t.id));
  const selectAll = () => {
    setSelected(prev => {
      const next = new Map(prev);
      if (trees.every(t => next.has(t.id))) trees.forEach(t => next.delete(t.id));
      else trees.forEach(t => next.set(t.id, t));
      return next;
    });
  };

  // ── BULK STATUS: Zustand für alle ausgewählten Bäume setzen ───────────────
  const handleBulkStatus = async () => {
    if (!bulkStatus) return;
    setBulkSaving(true);
    try {
      await Promise.all(Array.from(selected.keys()).map(id =>
        treesAPI.update(id, { zustand: bulkStatus })
      ));
      setSnackbar(`Zustand für ${selected.size} Bäume gesetzt: "${bulkStatus}"`);
      setBulkDialog(null); setBulkStatus(''); setSelected(new Map());
      loadTrees(pagination.page);
    } catch { setSnackbar('Fehler beim Aktualisieren'); }
    finally { setBulkSaving(false); }
  };

  // ── BULK FELL: Bäume als gefällt markieren ────────────────────────────────
  const handleBulkFell = async () => {
    setBulkSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await Promise.all(Array.from(selected.keys()).map(id =>
        treesAPI.update(id, { gefaellt: true, gefaellt_a: today })
      ));
      setSnackbar(`${selected.size} Bäume als gefällt markiert`);
      setBulkDialog(null); setSelected(new Map());
      loadTrees(pagination.page);
    } catch { setSnackbar('Fehler beim Markieren'); }
    finally { setBulkSaving(false); }
  };

  // ── BULK LV: Ausgewählte Bäume in bestehendes LV übernehmen ──────────────
  const handleBulkLV = async () => {
    if (!bulkLvId) return;
    setBulkSaving(true);
    try {
      // LV laden, ausgewählte Bäume als Positionen anhängen
      const lvRes = await api.get(`/lv/${bulkLvId}`);
      const lv = lvRes.data;
      const existingPos = lv.positionen || [];
      const selectedTrees = Array.from(selected.values());
      const newPositions = selectedTrees.map((t, i) => ({
        position_nr: existingPos.length + i + 1,
        measure_id: null,
        baumnummer: t.baumnummer,
        baumart: t.baumart,
        ort: t.ort,
        strasse: t.strasse,
        measure_type: null,
        description: null,
        planned_date: null,
        kosten: null,
      }));
      await api.put(`/lv/${bulkLvId}`, {
        ...lv,
        positionen: [...existingPos, ...newPositions],
      });
      setSnackbar(`${selected.size} Bäume zum LV "${lv.bezeichnung}" hinzugefügt`);
      setBulkDialog(null); setBulkLvId(''); setSelected(new Map());
    } catch { setSnackbar('Fehler beim Übernehmen ins LV'); }
    finally { setBulkSaving(false); }
  };

  // ── BULK EXPORT: Ausgewählte Bäume als CSV (clientseitig) ────────────────
  const handleBulkExport = () => {
    const selectedTrees = Array.from(selected.values());
    const headers = ['Baumnummer', 'Baumart', 'Ort', 'Strasse', 'Zustand', 'Höhe', 'Stammdurchmesser', 'Kronendurchmesser', 'Pflanzjahr', 'Bemerkung'];
    const rows = selectedTrees.map(t => [
      t.baumnummer || '', t.baumart || '', t.ort || '', t.strasse || '',
      t.zustand || '', t.hoehe || '', t.stdm || '', t.kdm || '',
      t.pflanzjahr || '', (t.bemerkung || '').replace(/[\r\n;]/g, ' '),
    ]);
    const csv = '\uFEFF' + [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `baumkataster_auswahl_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSnackbar(`${selected.size} Bäume als CSV exportiert`);
  };

  const handleBulkMeasure = async () => {
    if (!bulkMeasure.measure_type) return;
    setBulkSaving(true);
    try {
      const promises = Array.from(selected.keys()).map(treeId =>
        measuresAPI.create({ tree_id: treeId, ...bulkMeasure, assigned_to: bulkMeasure.assigned_to || null, status: 'offen' })
      );
      await Promise.all(promises);
      setSnackbar(`Maßnahme "${bulkMeasure.measure_type}" für ${selected.size} Bäume erstellt`);
      setBulkDialog(null); setBulkMeasure({ measure_type: '', description: '', planned_date: '', assigned_to: '' });
      setSelected(new Map()); loadTrees(pagination.page);
    } catch { setSnackbar('Fehler beim Erstellen der Maßnahmen'); }
    finally { setBulkSaving(false); }
  };

  const handleBulkDelete = async () => {
    setBulkSaving(true);
    try {
      const promises = Array.from(selected.keys()).map(id => treesAPI.delete(id));
      await Promise.all(promises);
      setSnackbar(`${selected.size} Bäume gelöscht`);
      setBulkDialog(null); setSelected(new Map()); loadTrees(1);
    } catch { setSnackbar('Fehler beim Löschen'); }
    finally { setBulkSaving(false); }
  };

  // Load filter options
  useEffect(() => {
    treesAPI.getFilterOptions().then(res => setFilterOptions(res.data)).catch(() => {});
  }, []);

  // Auto switch view on breakpoint
  useEffect(() => { setViewMode(isMobile ? 'cards' : 'table'); }, [isMobile]);

  // Builder für Filter-Params (genutzt von loadTrees und Export/Count)
  const buildFilterParams = useCallback(() => {
    const params: any = { sort_by: sortBy, sort_order: sortOrder };
    if (search) params.search = search;
    if (massnahmenStatus !== 'all') params.massnahmen_status = massnahmenStatus;
    if (schaedenStatus !== 'all') params.schaeden_status = schaedenStatus;
    if (kontrollenStatus !== 'all') params.kontrollen_status = kontrollenStatus;
    if (filterGefaellt) params.gefaellt = 'all';
    if (filterZustand) params.zustand = filterZustand;
    if (filterOrt) params.ort = filterOrt;
    if (filterMassnahmeArt) params.massnahme_art = filterMassnahmeArt;
    if (filterVerkehrssicherheit) params.verkehrssicherheit = filterVerkehrssicherheit;
    return params;
  }, [search, massnahmenStatus, schaedenStatus, kontrollenStatus, filterGefaellt, filterZustand, filterOrt, filterMassnahmeArt, filterVerkehrssicherheit, sortBy, sortOrder]);

  const loadTrees = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { ...buildFilterParams(), page, limit: 50 };
      const res = await treesAPI.getAll(params);
      setTrees(res.data.data);
      setPagination(res.data.pagination);
    } catch {
      setError('Bäume konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  useEffect(() => {
    const debounce = setTimeout(() => loadTrees(1), 300);
    return () => clearTimeout(debounce);
  }, [loadTrees]);

  // ── EXPORT (mit aktiven Filtern, alle Treffer) ───────────────────────────
  const [exportDialog, setExportDialog] = useState(false);
  const [exportCount, setExportCount] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const openExportDialog = async () => {
    setExportDialog(true);
    setExportCount(null);
    try {
      const res = await api.get('/trees/count', { params: buildFilterParams() });
      setExportCount(res.data.count);
    } catch {
      setExportCount(0);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/trees/export', {
        params: buildFilterParams(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `baumkataster_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setExportDialog(false);
      setSnackbar(`${exportCount} Bäume exportiert`);
    } catch {
      setSnackbar('Fehler beim Export');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (col: string) => {
    if (sortBy === col) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    else { setSortBy(col); setSortOrder('ASC'); }
  };

  const activeFilters = [massnahmenStatus !== 'all', schaedenStatus !== 'all', kontrollenStatus !== 'all', filterGefaellt, !!filterZustand, !!filterOrt, !!filterMassnahmeArt, !!filterVerkehrssicherheit].filter(Boolean).length;

  const clearAllFilters = () => {
    setMassnahmenStatus('all'); setSchaedenStatus('all'); setKontrollenStatus('all');
    setFilterGefaellt(false); setFilterZustand(''); setFilterOrt(''); setFilterMassnahmeArt(''); setFilterVerkehrssicherheit('');
    setSearch('');
  };

  const SortIcon: React.FC<{ col: string }> = ({ col }) => {
    if (sortBy !== col) return null;
    return sortOrder === 'ASC' ? <ArrowUpward sx={{ fontSize: 14, ml: 0.3 }} /> : <ArrowDownward sx={{ fontSize: 14, ml: 0.3 }} />;
  };

  return (
    <Box>
      {/* Search + Actions */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Suche: Baumnummer, Baumart, Ort..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20 }} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button size="small" variant={showFilters ? 'contained' : 'outlined'} startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          color={activeFilters > 0 ? 'primary' : 'inherit'}
          sx={{ minWidth: 'auto' }}>
          Filter {activeFilters > 0 && `(${activeFilters})`}
        </Button>
        {!isMobile && (
          <Box sx={{ display: 'flex', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            <IconButton size="small" onClick={() => setViewMode('table')}
              sx={{ borderRadius: 0, bgcolor: viewMode === 'table' ? 'primary.main' : 'transparent', color: viewMode === 'table' ? 'white' : 'inherit' }}>
              <ViewList sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setViewMode('cards')}
              sx={{ borderRadius: 0, bgcolor: viewMode === 'cards' ? 'primary.main' : 'transparent', color: viewMode === 'cards' ? 'white' : 'inherit' }}>
              <ViewModule sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        )}
        <Tooltip title="Aktuelle Filterung als CSV exportieren">
          <Button variant="outlined" startIcon={<Download />} onClick={openExportDialog} size="small" sx={{ minWidth: 'auto' }}>
            {isMobile ? '' : 'Export'}
          </Button>
        </Tooltip>
      </Box>

      {/* Filter Panel */}
      {showFilters && (
        <Box sx={{ p: 2, mb: 1.5, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1.2}>
            {/* Maßnahmen-Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600, color: 'text.secondary' }}>Maßnahmen:</Typography>
              <Chip label="Alle" size="small" variant={massnahmenStatus === 'all' ? 'filled' : 'outlined'}
                color={massnahmenStatus === 'all' ? 'primary' : 'default'}
                onClick={() => setMassnahmenStatus('all')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<Schedule sx={{ fontSize: 16 }} />} label="Offen" size="small"
                variant={massnahmenStatus === 'open' ? 'filled' : 'outlined'}
                color={massnahmenStatus === 'open' ? 'warning' : 'default'}
                onClick={() => setMassnahmenStatus('open')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label="Erledigt" size="small"
                variant={massnahmenStatus === 'done' ? 'filled' : 'outlined'}
                color={massnahmenStatus === 'done' ? 'success' : 'default'}
                onClick={() => setMassnahmenStatus('done')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<RemoveCircleOutline sx={{ fontSize: 16 }} />} label="Keine" size="small"
                variant={massnahmenStatus === 'none' ? 'filled' : 'outlined'}
                color={massnahmenStatus === 'none' ? 'default' : 'default'}
                onClick={() => setMassnahmenStatus('none')} sx={{ cursor: 'pointer' }} />
            </Box>

            {/* Schäden-Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600, color: 'text.secondary' }}>Schäden:</Typography>
              <Chip label="Alle" size="small" variant={schaedenStatus === 'all' ? 'filled' : 'outlined'}
                color={schaedenStatus === 'all' ? 'primary' : 'default'}
                onClick={() => setSchaedenStatus('all')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<Warning sx={{ fontSize: 16 }} />} label="Offen" size="small"
                variant={schaedenStatus === 'open' ? 'filled' : 'outlined'}
                color={schaedenStatus === 'open' ? 'warning' : 'default'}
                onClick={() => setSchaedenStatus('open')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label="Behoben" size="small"
                variant={schaedenStatus === 'behoben' ? 'filled' : 'outlined'}
                color={schaedenStatus === 'behoben' ? 'success' : 'default'}
                onClick={() => setSchaedenStatus('behoben')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<RemoveCircleOutline sx={{ fontSize: 16 }} />} label="Keine" size="small"
                variant={schaedenStatus === 'none' ? 'filled' : 'outlined'}
                color={schaedenStatus === 'none' ? 'default' : 'default'}
                onClick={() => setSchaedenStatus('none')} sx={{ cursor: 'pointer' }} />
            </Box>

            {/* Kontrollen-Status (erweitert v1.5.0: zeitbasierte Filter) */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600, color: 'text.secondary' }}>Kontrollen:</Typography>
              <Chip label="Alle" size="small" variant={kontrollenStatus === 'all' ? 'filled' : 'outlined'}
                color={kontrollenStatus === 'all' ? 'primary' : 'default'}
                onClick={() => setKontrollenStatus('all')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label="Aktuell" size="small"
                variant={kontrollenStatus === 'aktuell' ? 'filled' : 'outlined'}
                color={kontrollenStatus === 'aktuell' ? 'success' : 'default'}
                onClick={() => setKontrollenStatus('aktuell')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<Schedule sx={{ fontSize: 16 }} />} label="Bald fällig" size="small"
                variant={kontrollenStatus === 'soon' ? 'filled' : 'outlined'}
                color={kontrollenStatus === 'soon' ? 'warning' : 'default'}
                onClick={() => setKontrollenStatus('soon')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<Warning sx={{ fontSize: 16 }} />} label="Überfällig" size="small"
                variant={kontrollenStatus === 'overdue' ? 'filled' : 'outlined'}
                color={kontrollenStatus === 'overdue' ? 'error' : 'default'}
                onClick={() => setKontrollenStatus('overdue')} sx={{ cursor: 'pointer' }} />
              <Chip icon={<EventBusy sx={{ fontSize: 16 }} />} label="Nie kontrolliert" size="small"
                variant={kontrollenStatus === 'never' ? 'filled' : 'outlined'}
                color={kontrollenStatus === 'never' ? 'error' : 'default'}
                onClick={() => setKontrollenStatus('never')} sx={{ cursor: 'pointer' }} />
            </Box>
          </Stack>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.8, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Zustand</InputLabel>
              <Select value={filterZustand} label="Zustand" onChange={e => setFilterZustand(e.target.value)}>
                <MenuItem value="">Alle</MenuItem>
                {filterOptions.zustaende?.map((z: string) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Ort</InputLabel>
              <Select value={filterOrt} label="Ort" onChange={e => setFilterOrt(e.target.value)}>
                <MenuItem value="">Alle</MenuItem>
                {filterOptions.orte?.map((o: string) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 210 }}>
              <InputLabel>Offene Maßnahmen-Art</InputLabel>
              <Select value={filterMassnahmeArt} label="Offene Maßnahmen-Art" onChange={e => setFilterMassnahmeArt(e.target.value)}>
                <MenuItem value="">Alle</MenuItem>
                {MEASURE_TYPE_GROUPS.flatMap((group) => [
                  <ListSubheader key={`fhdr-${group.label}`} sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', lineHeight: '28px', bgcolor: 'background.paper' }}>{group.label}</ListSubheader>,
                  ...group.items.map(m => <MenuItem key={m.value} value={m.value} sx={{ pl: 3 }}>{m.value}</MenuItem>),
                ])}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 175 }}>
              <InputLabel>Verkehrssicherheit</InputLabel>
              <Select value={filterVerkehrssicherheit} label="Verkehrssicherheit" onChange={e => setFilterVerkehrssicherheit(e.target.value)}>
                <MenuItem value="">Alle</MenuItem>
                {VERKEHRSSICHERHEIT_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
            <Chip label="Gefällte anzeigen" size="small"
              variant={filterGefaellt ? 'filled' : 'outlined'}
              color={filterGefaellt ? 'warning' : 'default'}
              onClick={() => setFilterGefaellt(!filterGefaellt)} sx={{ cursor: 'pointer' }} />
            <Box sx={{ flex: 1 }} />
            {activeFilters > 0 && (
              <Button size="small" onClick={clearAllFilters} sx={{ fontSize: '0.78rem' }}>
                Alle Filter zurücksetzen
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Result count */}
      <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 1.5, px: 2, py: 0.8, mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '0.82rem' }}>
          {loading ? 'Lade...' : `${pagination.total.toLocaleString('de-DE')} Bäume`}
          {pagination.totalPages > 1 && ` · Seite ${pagination.page}/${pagination.totalPages}`}
          {activeFilters > 0 && ` · ${activeFilters} Filter aktiv`}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ═══ TABLE VIEW ═══ */}
      {viewMode === 'table' && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                {canEdit && (
                  <TableCell padding="checkbox" sx={{ width: 40 }}>
                    <Checkbox size="small" checked={currentPageAllSelected}
                      indeterminate={currentPageSomeSelected && !currentPageAllSelected}
                      onChange={selectAll} />
                  </TableCell>
                )}
                <TableCell onClick={() => handleSort('baumnummer')} sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
                  Nr. <SortIcon col="baumnummer" />
                </TableCell>
                <TableCell onClick={() => handleSort('baumart')} sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
                  Baumart <SortIcon col="baumart" />
                </TableCell>
                <TableCell onClick={() => handleSort('ort')} sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
                  Ort <SortIcon col="ort" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Straße</TableCell>
                <TableCell onClick={() => handleSort('zustand')} sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
                  Zustand <SortIcon col="zustand" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Verkehrss.</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textAlign: 'center' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textAlign: 'center' }}>Kontrollen</TableCell>
                <TableCell onClick={() => handleSort('updated_at')} sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
                  Aktualisiert <SortIcon col="updated_at" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 9 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                ))
              ) : trees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 10 : 9} sx={{ p: 0, border: 0 }}>
                    {activeFilters > 0 ? (
                      <EmptyState
                        icon={SearchOff}
                        title="Keine Bäume gefunden"
                        description="Mit den aktuellen Filtern wurde kein passender Baum gefunden. Filter anpassen oder zurücksetzen."
                        action={{ label: 'Alle Filter zurücksetzen', onClick: clearAllFilters }}
                        accentColor="info"
                      />
                    ) : (
                      <EmptyState
                        icon={Park}
                        title="Noch keine Bäume erfasst"
                        description="Lege deinen ersten Baum auf der Karte an oder importiere eine CSV-Datei mit bestehenden Daten."
                        action={canEdit ? { label: 'Auf der Karte anlegen', icon: Add, onClick: () => navigate('/map') } : undefined}
                        accentColor="primary"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                trees.map(tree => {
                  const hatSchaden = tree.offene_schaeden > 0 || tree.scha_o_1;
                  return (
                    <TableRow key={tree.id} hover sx={{
                      cursor: 'pointer', opacity: tree.gefaellt ? 0.5 : 1,
                      borderLeft: hatSchaden ? '4px solid #ff9800' : '4px solid transparent',
                      bgcolor: selected.has(tree.id) ? 'primary.main' + '08' : undefined,
                    }} onClick={() => navigate(`/trees/${tree.id}`)}>
                      {canEdit && (
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <Checkbox size="small" checked={selected.has(tree.id)} onChange={() => toggleSelect(tree)} />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Typography fontWeight={700} sx={{ fontSize: '0.85rem' }}>{tree.baumnummer}</Typography>
                          {tree.is_baumgruppe && (
                            <Chip label="Gruppe" size="small" color="primary"
                              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>
                        {tree.is_baumgruppe
                          ? <Typography component="span" sx={{ fontSize: '0.82rem', fontStyle: 'italic', color: 'text.secondary' }}>
                              Gruppe{tree.gruppe_anzahl ? ` · ${tree.gruppe_anzahl} Bäume` : ''}
                            </Typography>
                          : (tree.baumart || '–')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{tree.ort || '–'}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{tree.strasse || '–'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getZustandColor(tree.zustand), flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{tree.zustand || '–'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {tree.verkehrssicherheit ? (
                          <Chip label={tree.verkehrssicherheit} size="small" sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, color: getVerkehrssicherheitColor(tree.verkehrssicherheit).color, bgcolor: getVerkehrssicherheitColor(tree.verkehrssicherheit).bg }} />
                        ) : <Typography variant="caption" color="text.secondary">–</Typography>}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center' }}>
                          {hatSchaden && <Tooltip title={`${tree.offene_schaeden || ''} Schäden`}><Warning sx={{ color: '#ff9800', fontSize: 18 }} /></Tooltip>}
                          {!hatSchaden && tree.bestaetigte_schaeden > 0 && <Tooltip title={`${tree.bestaetigte_schaeden} geprüft, keine offenen Schäden`}><CheckCircle sx={{ color: '#2e7d32', fontSize: 18 }} /></Tooltip>}
                          {tree.offene_massnahmen > 0 && <Tooltip title={`${tree.offene_massnahmen} Maßnahmen`}><Star sx={{ color: '#1976d2', fontSize: 18 }} /></Tooltip>}
                          {tree.gefaellt && <Chip label="Gefällt" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {tree.kontrollen_count > 0 ? (
                          <Tooltip title={`Letzte: ${tree.letzte_kontrolle ? new Date(tree.letzte_kontrolle).toLocaleDateString('de-DE') : '–'}`}>
                            <Chip label={tree.kontrollen_count} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">–</Typography>
                        )}
                      </TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{new Date(tree.updated_at).toLocaleDateString('de-DE')}</Typography></TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ═══ CARD VIEW ═══ */}
      {viewMode === 'cards' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={80} sx={{ borderRadius: 2 }} />)
          ) : trees.length === 0 ? (
            activeFilters > 0 ? (
              <EmptyState
                icon={SearchOff}
                title="Keine Bäume gefunden"
                description="Mit den aktuellen Filtern wurde kein passender Baum gefunden."
                action={{ label: 'Alle Filter zurücksetzen', onClick: clearAllFilters }}
                accentColor="info"
              />
            ) : (
              <EmptyState
                icon={Park}
                title="Noch keine Bäume erfasst"
                description="Lege deinen ersten Baum auf der Karte an oder importiere eine CSV-Datei."
                action={canEdit ? { label: 'Auf der Karte anlegen', icon: Add, onClick: () => navigate('/map') } : undefined}
                accentColor="primary"
              />
            )
          ) : (
            trees.map(tree => (
              <TreeCard key={tree.id} tree={tree} onClick={() => navigate(`/trees/${tree.id}`)} />
            ))
          )}
        </Box>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
          <Pagination count={pagination.totalPages} page={pagination.page}
            onChange={(_, page) => loadTrees(page)} color="primary"
            size={isMobile ? 'small' : 'medium'}
            showFirstButton showLastButton />
        </Box>
      )}

      {/* Abstandhalter: reserviert Platz, damit die fixierte Auswahl-Leiste die
          Pagination / letzten Zeilen nicht verdeckt (Mobile: über der Bottom-Nav). */}
      {selected.size > 0 && <Box sx={{ height: { xs: 140, md: 84 } }} />}

      {/* Bulk Measure Dialog */}
      <Dialog open={bulkDialog === 'measure'} onClose={() => setBulkDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4a7c59', color: 'white', py: 1.5 }}>
          Maßnahme für {selected.size} Bäume zuweisen
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Maßnahmentyp *</InputLabel>
            <Select value={bulkMeasure.measure_type} label="Maßnahmentyp *" onChange={e => setBulkMeasure({...bulkMeasure, measure_type: e.target.value})}>
              {MEASURE_TYPE_GROUPS.flatMap((group) => [
                <ListSubheader key={`hdr-${group.label}`} sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', lineHeight: '28px', bgcolor: 'background.paper' }}>
                  {group.label}
                </ListSubheader>,
                ...group.items.map(t => (
                  <MenuItem key={t.value} value={t.value} sx={{ pl: 3, py: 0.7 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.value}</Typography>
                      {t.beschreibung !== t.value && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {t.beschreibung}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          <TextField fullWidth size="small" label="Beschreibung" multiline rows={2}
            value={bulkMeasure.description} onChange={e => setBulkMeasure({...bulkMeasure, description: e.target.value})} />
          <TextField fullWidth size="small" label="Geplantes Datum" type="date" InputLabelProps={{ shrink: true }}
            value={bulkMeasure.planned_date} onChange={e => setBulkMeasure({...bulkMeasure, planned_date: e.target.value})} />
          <FormControl fullWidth size="small">
            <InputLabel shrink>Zuweisen an</InputLabel>
            <Select label="Zuweisen an" notched displayEmpty value={bulkMeasure.assigned_to}
              onChange={e => setBulkMeasure({...bulkMeasure, assigned_to: e.target.value})}>
              <MenuItem value=""><em>– niemandem –</em></MenuItem>
              {selectableUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#f5f5f5', px: 3, py: 1.5 }}>
          <Button variant="contained" onClick={handleBulkMeasure} disabled={bulkSaving || !bulkMeasure.measure_type}
            sx={{ bgcolor: '#4a7c59' }}>
            {bulkSaving ? 'Wird erstellt...' : `Für ${selected.size} Bäume erstellen`}
          </Button>
          <Button onClick={() => setBulkDialog(null)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDialog === 'delete'} onClose={() => setBulkDialog(null)}>
        <DialogTitle color="error">Bäume löschen?</DialogTitle>
        <DialogContent>
          <Typography>Sollen wirklich <strong>{selected.size} Bäume</strong> unwiderruflich gelöscht werden?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleBulkDelete} disabled={bulkSaving}>
            {bulkSaving ? 'Wird gelöscht...' : `${selected.size} Bäume löschen`}
          </Button>
          <Button onClick={() => setBulkDialog(null)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ BULK ACTION BAR – fest am unteren Rand, immer Full-Width ═══
          Liegt über der BottomNavigation (zIndex 1300 vs 1200). Bei Mobile
          rutscht sie über die Bottom-Nav (bottom: 56px), auf Desktop sitzt
          sie ganz unten. Safe-Area für iPhone-Notch berücksichtigt. */}
      <Slide direction="up" in={selected.size > 0} mountOnEnter unmountOnExit>
        <MuiPaper
          elevation={12}
          sx={{
            position: 'fixed',
            zIndex: 1300,
            left: 0,
            right: 0,
            bottom: { xs: '56px', md: 0 },
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 0,
            borderTop: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.25)',
            pb: 'env(safe-area-inset-bottom)',
          }}
        >
          <Box sx={{
            maxWidth: 1400,
            mx: 'auto',
            px: { xs: 1.5, md: 3 },
            py: { xs: 1, md: 1.2 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 2 },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
          }}>
            {/* Status-Block: "X ausgewählt" */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              pr: { md: 2 },
              borderRight: { md: '1px solid rgba(255,255,255,0.2)' },
              flexShrink: 0,
            }}>
              <CheckBoxIcon sx={{ fontSize: 22 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  {selected.size} {selected.size === 1 ? 'Baum' : 'Bäume'} ausgewählt
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, display: { xs: 'none', md: 'block' }, fontSize: '0.7rem' }}>
                  Aktion für alle anwenden
                </Typography>
              </Box>
            </Box>

            {/* Aktionen */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 },
              flex: 1, flexShrink: 0,
            }}>
              {canEdit && (
                <Button
                  size="small"
                  startIcon={<Edit sx={{ fontSize: 18 }} />}
                  onClick={() => setBulkDialog('status')}
                  sx={{ color: 'white', textTransform: 'none', fontWeight: 600, px: { xs: 1, md: 1.5 }, minHeight: 36, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                >
                  Zustand
                </Button>
              )}
              {canEdit && (
                <Button
                  size="small"
                  startIcon={<FolderOpen sx={{ fontSize: 18 }} />}
                  onClick={() => { setBulkDialog('lv'); loadLVs(); }}
                  sx={{ color: 'white', textTransform: 'none', fontWeight: 600, px: { xs: 1, md: 1.5 }, minHeight: 36, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                >
                  In LV
                </Button>
              )}
              {canEdit && (
                <Button
                  size="small"
                  startIcon={<Assignment sx={{ fontSize: 18 }} />}
                  onClick={() => setBulkDialog('measure')}
                  sx={{ color: 'white', textTransform: 'none', fontWeight: 600, px: { xs: 1, md: 1.5 }, minHeight: 36, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                >
                  Maßnahme
                </Button>
              )}
              {canEdit && (
                <Button
                  size="small"
                  startIcon={<ContentCut sx={{ fontSize: 18 }} />}
                  onClick={() => setBulkDialog('fell')}
                  sx={{ color: 'white', textTransform: 'none', fontWeight: 600, px: { xs: 1, md: 1.5 }, minHeight: 36, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                >
                  Gefällt
                </Button>
              )}
              <Button
                size="small"
                startIcon={<Download sx={{ fontSize: 18 }} />}
                onClick={handleBulkExport}
                sx={{ color: 'white', textTransform: 'none', fontWeight: 600, px: { xs: 1, md: 1.5 }, minHeight: 36, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
              >
                Export
              </Button>
              {isAdmin && (
                <Button
                  size="small"
                  startIcon={<Delete sx={{ fontSize: 18 }} />}
                  onClick={() => setBulkDialog('delete')}
                  sx={{ color: '#ffcdd2', textTransform: 'none', fontWeight: 600, px: { xs: 1, md: 1.5 }, minHeight: 36, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,82,82,0.2)' } }}
                >
                  Löschen
                </Button>
              )}
            </Box>

            {/* Schließen-Button */}
            <IconButton
              size="small"
              onClick={() => setSelected(new Map())}
              sx={{ color: 'white', flexShrink: 0, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
              aria-label="Auswahl aufheben"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </MuiPaper>
      </Slide>

      {/* ═══ BULK STATUS DIALOG ═══ */}
      <Dialog open={bulkDialog === 'status'} onClose={() => setBulkDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Zustand für {selected.size} Bäume setzen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Der gewählte Zustand wird für alle markierten Bäume gesetzt.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Zustand</InputLabel>
            <Select value={bulkStatus} label="Zustand" onChange={e => setBulkStatus(e.target.value)}>
              {(filterOptions.zustaende || []).map((z: string) => (
                <MenuItem key={z} value={z}>{z}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleBulkStatus} disabled={!bulkStatus || bulkSaving}>
            {bulkSaving ? <CircularProgress size={20} /> : `Auf ${selected.size} Bäume anwenden`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ BULK FELL DIALOG ═══ */}
      <Dialog open={bulkDialog === 'fell'} onClose={() => setBulkDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{selected.size} Bäume als gefällt markieren</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Diese Aktion markiert alle ausgewählten Bäume als gefällt und setzt das Fälldatum auf heute. Die Bäume bleiben im System, werden aber in der Karte und Liste optisch ausgegraut.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog(null)}>Abbrechen</Button>
          <Button variant="contained" color="warning" startIcon={<ContentCut />} onClick={handleBulkFell} disabled={bulkSaving}>
            {bulkSaving ? <CircularProgress size={20} /> : `${selected.size} markieren`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ BULK LV DIALOG ═══ */}
      <Dialog open={bulkDialog === 'lv'} onClose={() => setBulkDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected.size} Bäume in Leistungsverzeichnis übernehmen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Die Bäume werden als neue Positionen ohne Maßnahme angehängt. Maßnahme und Kosten können danach direkt im LV gepflegt werden.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Leistungsverzeichnis</InputLabel>
            <Select value={bulkLvId} label="Leistungsverzeichnis" onChange={e => setBulkLvId(e.target.value)}>
              {availableLVs.length === 0 && <MenuItem disabled value="">Lädt…</MenuItem>}
              {availableLVs.map((lv: any) => (
                <MenuItem key={lv.id} value={lv.id}>
                  <ListItemText
                    primary={lv.bezeichnung}
                    secondary={`${lv.ort || '–'} · ${lv.anzahl_positionen || 0} Positionen · ${lv.status}`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleBulkLV} disabled={!bulkLvId || bulkSaving}>
            {bulkSaving ? <CircularProgress size={20} /> : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ EXPORT-BESTÄTIGUNG-DIALOG ═══ */}
      <Dialog open={exportDialog} onClose={() => !exporting && setExportDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>CSV-Export</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Exportiert die aktuell gefilterten Bäume als CSV-Datei mit allen wichtigen Feldern (Baumnummer, Baumart, Standort, Zustand, Maße, Pflanzjahr, Koordinaten, Bemerkung).
          </Typography>
          {exportCount === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Anzahl wird ermittelt...</Typography>
            </Box>
          ) : (
            <Alert severity={exportCount > 5000 ? 'warning' : 'info'} icon={<Download />}>
              Diese Filterung umfasst <strong>{exportCount.toLocaleString('de-DE')}</strong> {exportCount === 1 ? 'Baum' : 'Bäume'}.
              {exportCount > 5000 && ' Das Erzeugen der Datei kann etwas dauern.'}
              {exportCount === 0 && ' Es gibt nichts zu exportieren.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)} disabled={exporting}>Abbrechen</Button>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <Download />}
            onClick={doExport}
            disabled={exporting || exportCount === null || exportCount === 0}
          >
            {exporting ? 'Wird erzeugt...' : 'Jetzt exportieren'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar('')}
        message={snackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
